/**
 * ArtworkUrlSection — heal a custom print-file issue from an operator-pasted
 * artwork URL (Google Drive share link, Google Cloud Storage, Amazon S3,
 * Shopify CDN).
 *
 * The backend fetches the image server-side through the hardened artwork
 * boundary, resizes it for the SKU's platen, ingests it into the print cloud
 * under the issue's expected filename, verifies, and resolves the issue. For
 * legacy customs with NO design reference at all it first mints one onto the
 * order line, so the print station can resolve the file from then on.
 *
 * RIP (.arx4) locations cannot be healed server-side — the heal REQUEUES the
 * bootleg-bridge instead (`status:'requeued'`). BLU-880: from that moment the
 * section tracks the bridge live (`useBridgeFetchProgress` — SSE + a 15s poll)
 * and renders a stepper above the form's submit button, which stays mounted
 * but disabled ("Fetching…"): Sent → Picked up → Downloaded → RIPped →
 * Uploaded → Done. A `failed` step, or a stale run (150 s before pickup, 5
 * minutes after), re-enables the
 * button as "Try again" (the same heal; falls
 * back to Self-Heal Now when no URL was pasted — e.g. the dialog was reopened
 * on a run the list row started). `resolved` fires the healed toast +
 * `onResolved`.
 *
 * BLU-1309: a paste is downloaded and checked by the server before anything is
 * queued, and a run this section started ends in a notification to whoever
 * started it, so after a `requeued` or `healing` answer it says the window may
 * be closed (`backgroundHint`). A run only seeded from the row (someone else's,
 * or an earlier session's) does not say that. `healing` (the server is still
 * working after 20 s) never starts bridge tracking (F3): a static line says so.
 *
 * Used by ResolveDialog in two places: as a section inside the file_missing
 * (ondemand) dialog alongside Recheck + Upload, and as the primary form for
 * the legacy `missing_artwork` custom shape.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing, Loader2, Link2, CheckCircle2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { useBridgeFetchProgress } from '../hooks/useBridgeFetchProgress';
import { BridgeFetchStepper } from './BridgeFetchStepper';
import {
  healFailReasonKey,
  isNothingToRetryReason,
  isTerminalBridgeFetchState,
  type BridgeFetchSnapshot,
} from '../bridgeFetchState';

interface ArtworkUrlSectionProps {
  issueId: string;
  /** The issue's location — scopes the live bridge-progress stream. */
  locationId?: string | null;
  /** Show the optional back-side URL field (front+back customs). */
  showBack?: boolean;
  /** External in-flight guard — e.g. a sibling "convert to upload" mutation
   * pending in the same dialog. Disables the fields + submit. */
  disabled?: boolean;
  /**
   * BLU-880: a bridge run already in flight on this issue when the dialog
   * opened (from the row's `bridge_fetch_state`), so reopening the dialog does
   * not lose progress. The stepper renders read-only from it straight away.
   */
  initialBridgeFetch?: BridgeFetchSnapshot | null;
  /** Notified whenever this section's own heal request starts/finishes, so a
   * sibling control (e.g. ResolveDialog's "Upload the file instead" button)
   * can disable itself while a heal is in flight. */
  onHealingChange?: (isHealing: boolean) => void;
  /** BLU-880: notified when a bridge fetch starts being tracked / reaches a
   * terminal state, so the dialog can hide the manual upload zone while the
   * bridge is working the file (an upload would race the RIP). */
  onBridgeFetchChange?: (inFlight: boolean) => void;
  onResolved: () => void;
}

const URL_RE = /^https:\/\/\S+$/i;

export function ArtworkUrlSection({
  issueId,
  locationId,
  showBack = false,
  disabled = false,
  initialBridgeFetch = null,
  onHealingChange,
  onBridgeFetchChange,
  onResolved,
}: ArtworkUrlSectionProps) {
  const { t } = useTranslation('production');

  const [frontUrl, setFrontUrl] = useState('');
  const [backUrl, setBackUrl] = useState('');
  const [isHealing, setIsHealing] = useState(false);
  const [message, setMessage] = useState<{ kind: 'error' | 'info'; text: string } | null>(null);
  // Bridge tracking is on from the first `requeued` (or from a seeded state)
  // until the run reaches a terminal state AND the operator retries.
  const [tracking, setTracking] = useState(initialBridgeFetch !== null);
  // BLU-1309: this section's own submit started the tracked run.
  const [startedHere, setStartedHere] = useState(false);
  // F3: the server answered `healing` (still working after 20 s).
  const [stillWorking, setStillWorking] = useState(false);

  const healMutation = trpc.printAgentAdmin.healCustomFromArtworkUrl.useMutation();
  const selfHealMutation = trpc.printAgentAdmin.selfHealCustomNow.useMutation();
  const utils = trpc.useUtils();

  const finishResolved = () => {
    toast.success(t('issues.printFiles.artworkUrl.healed'));
    utils.productionOrders.issues.list.invalidate();
    utils.productionOrders.issues.getCounts.invalidate();
    onResolved();
  };

  const bridge = useBridgeFetchProgress(issueId, locationId, {
    enabled: tracking,
    initial: initialBridgeFetch,
    onResolved: finishResolved,
  });

  const bridgeTerminal = bridge.snapshot ? isTerminalBridgeFetchState(bridge.snapshot.state) : false;
  const bridgeFailed = bridge.snapshot?.state === 'failed';
  // "In flight" = tracked, not terminal, and the bridge is still responsive.
  const bridgeInFlight = tracking && !bridgeTerminal && !bridge.timedOut;
  // The operator may act again once the run failed or went quiet.
  const canRetry = tracking && (bridgeFailed || bridge.timedOut);
  // `no_source_url` / `no_filename`: a bare "Try again" would re-run the same
  // dead end — the operator has to paste a link (or upload) first.
  // Tracked runs only: an untracked snapshot is an earlier run (R2-2).
  const nothingToRetry = tracking && bridgeFailed && isNothingToRetryReason(bridge.snapshot?.reason ?? null);
  // F1b: never picked up and gone quiet. Its own line says what to do; "you
  // can close this window" would contradict it.
  const notPickedUp = bridge.timedOut && bridge.snapshot?.state === 'queued';

  useEffect(() => {
    onBridgeFetchChange?.(bridgeInFlight);
  }, [bridgeInFlight, onBridgeFetchChange]);

  const setHealing = (value: boolean) => {
    setIsHealing(value);
    onHealingChange?.(value);
  };

  const front = frontUrl.trim();
  const back = backUrl.trim();
  const frontInvalid = front.length > 0 && !URL_RE.test(front);
  const backInvalid = back.length > 0 && !URL_RE.test(back);
  // A retry with no pasted URL falls back to Self-Heal Now (the order line's
  // own artwork URL) — the same executor, the same bridge requeue.
  const retryWithoutUrl = canRetry && !nothingToRetry && front.length === 0 && !backInvalid;
  // Offer "Try again" only when there is something to retry: a pasted URL, or
  // a failure the same source might get past next time.
  const offerRetry = canRetry && (front.length > 0 || !nothingToRetry);
  const submitDisabled =
    isHealing ||
    disabled ||
    bridgeInFlight ||
    frontInvalid ||
    backInvalid ||
    (front.length === 0 && !retryWithoutUrl);

  // F26: the same reason map as the Self-Heal Now toast.
  const failReasonText = (reason: string): string =>
    t(healFailReasonKey(reason, 'issues.printFiles.artworkUrl.failGeneric'));

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setHealing(true);
    setMessage(null);
    setStillWorking(false);
    try {
      const res = retryWithoutUrl
        ? await selfHealMutation.mutateAsync({ issueId })
        : await healMutation.mutateAsync({
            issueId,
            frontUrl: front || null,
            backUrl: back || null,
          });
      if (res.status === 'healed') {
        setTracking(false);
        finishResolved();
        return;
      }
      if (res.status === 'requeued') {
        // RIP location: the bridge owns the file from here. Track it live: the
        // stepper replaces the static "queued" line, and the person may close
        // the window.
        bridge.reset();
        setTracking(true);
        setStartedHere(true);
        return;
      }
      if (res.status === 'healing') {
        // F3: the SERVER is still working after 20 s; nothing was handed to a
        // bridge yet, so no stepper (a stale row stamp would win the merge,
        // and a non-bridge floor would show a stepper it never uses). A
        // static line instead; the bell tells them how it ends.
        // R2-2: the dialog may have opened on an earlier run's failure, which
        // turned tracking on; that run's stepper and "Try again" are stale now.
        setTracking(false);
        setStartedHere(false);
        setStillWorking(true);
        return;
      }
      setTracking(false);
      setStartedHere(false);
      setMessage({ kind: 'error', text: failReasonText(res.reason) });
    } catch (err) {
      setMessage({ kind: 'error', text: getUserMessage(err) });
    } finally {
      setHealing(false);
    }
  };

  const fieldsDisabled = isHealing || disabled || bridgeInFlight;
  const showStepper = tracking && bridge.snapshot !== null;

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-threadheads-purple/30 bg-threadheads-purple/5 p-3">
      <div className="flex items-start gap-2">
        <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-threadheads-purple" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium">{t('issues.printFiles.artworkUrl.title')}</p>
          <p className="text-xs text-muted-foreground">{t('issues.printFiles.artworkUrl.hint')}</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="space-y-1.5">
          <Label htmlFor="artwork-url-front" className="text-xs font-medium">
            {showBack
              ? t('issues.printFiles.artworkUrl.frontLabel')
              : t('issues.printFiles.artworkUrl.imageLabel')}
          </Label>
          <Input
            id="artwork-url-front"
            type="url"
            inputMode="url"
            value={frontUrl}
            placeholder={t('issues.printFiles.artworkUrl.placeholder')}
            className="font-mono text-xs"
            disabled={fieldsDisabled}
            aria-invalid={frontInvalid || undefined}
            onChange={(e) => setFrontUrl(e.target.value)}
          />
        </div>
        {showBack && (
          <div className="space-y-1.5">
            <Label htmlFor="artwork-url-back" className="text-xs font-medium">
              {t('issues.printFiles.artworkUrl.backLabel')}
            </Label>
            <Input
              id="artwork-url-back"
              type="url"
              inputMode="url"
              value={backUrl}
              placeholder={t('issues.printFiles.artworkUrl.placeholder')}
              className="font-mono text-xs"
              disabled={fieldsDisabled}
              aria-invalid={backInvalid || undefined}
              onChange={(e) => setBackUrl(e.target.value)}
            />
          </div>
        )}
      </div>

      {(frontInvalid || backInvalid) && (
        <p className="text-xs text-destructive" role="alert">
          {t('issues.printFiles.artworkUrl.invalidUrl')}
        </p>
      )}
      {message && (
        <p
          className={`text-xs ${message.kind === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
          role={message.kind === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {message.text}
        </p>
      )}

      {showStepper && bridge.snapshot && (
        <div className="rounded-md border border-border/60 bg-background/60 px-3 pt-3">
          <BridgeFetchStepper
            snapshot={bridge.snapshot}
            failedStep={bridge.failedStep}
            timedOut={bridge.timedOut}
          />
        </div>
      )}

      {stillWorking && (
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-foreground"
          data-testid="artwork-url-still-working"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {t('issues.printFiles.selfHealStillWorking')}
        </p>
      )}

      {(stillWorking || (startedHere && tracking && !bridgeTerminal && !notPickedUp)) && (
        <p
          role="status"
          className="flex items-start gap-2 break-words text-sm text-foreground"
          data-testid="artwork-url-background-hint"
        >
          <BellRing className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('issues.printFiles.artworkUrl.backgroundHint')}</span>
        </p>
      )}

      {nothingToRetry && front.length === 0 && (
        // F23: an issue missing its order or SKU cannot take a pasted link
        // either; only an upload helps.
        bridge.snapshot?.reason === 'issue_incomplete' ? (
          <p className="text-xs text-muted-foreground" data-testid="bridge-fetch-upload-only">
            {t('issues.printFiles.bridgeFetch.uploadOnly')}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="bridge-fetch-nothing-to-retry">
            {t('issues.printFiles.bridgeFetch.nothingToRetry')}
          </p>
        )
      )}

      {/* The button stays MOUNTED throughout (unmounting it dropped keyboard
          focus): while the bridge is working the file it is disabled and reads
          "Fetching…" — the stepper IS the status — and it comes back as
          "Try again" on failure or silence. */}
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={submitDisabled}
          aria-busy={bridgeInFlight || isHealing || undefined}
          className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
        >
          {isHealing || bridgeInFlight ? (
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : offerRetry ? (
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          )}
          {isHealing
            ? t('issues.printFiles.artworkUrl.checking')
            : bridgeInFlight
              ? t('issues.printFiles.bridgeFetch.fetching')
              : offerRetry
                ? t('issues.printFiles.bridgeFetch.tryAgain')
                : t('issues.printFiles.artworkUrl.submit')}
        </Button>
      </div>
    </div>
  );
}
